import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, getDoc, getDocFromServer, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import logger from '../utils/logger';

interface MaintenanceSettings {
  enabled: boolean;
  message?: string;
  estimatedTime?: string;
  lastUpdated?: string;
  updatedBy?: string;
}

interface MaintenanceContextType {
  isMaintenanceEnabled: boolean;
  maintenanceSettings: MaintenanceSettings;
  toggleMaintenance: () => Promise<void>;
  updateMaintenanceSettings: (settings: Partial<MaintenanceSettings>) => Promise<void>;
  loading: boolean;
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined);

export const useMaintenance = () => {
  const context = useContext(MaintenanceContext);
  if (context === undefined) {
    // Return default values instead of throwing error during development
    console.warn('useMaintenance must be used within a MaintenanceProvider. Using defaults.');
    return {
      isMaintenanceEnabled: false,
      maintenanceSettings: {
        enabled: false,
        message: "We're working hard to improve your experience!",
        estimatedTime: "2-3 hours"
      },
      toggleMaintenance: async () => {},
      updateMaintenanceSettings: async () => {},
      loading: false
    };
  }
  return context;
};

interface MaintenanceProviderProps {
  children: ReactNode;
}

export const MaintenanceProvider: React.FC<MaintenanceProviderProps> = ({ children }) => {
  // Initialize from localStorage to prevent flash on page load
  const getCachedMaintenanceState = (): { enabled: boolean; settings: MaintenanceSettings } => {
    try {
      const cached = localStorage.getItem('maintenanceState');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Check if cache is less than 5 minutes old
        if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
          return { enabled: parsed.enabled || false, settings: parsed.settings || {
            enabled: false,
            message: "We're working hard to improve your experience!",
            estimatedTime: "2-3 hours"
          }};
        }
      }
    } catch (error) {
      logger.debug('MaintenanceContext: Error reading cache:', error);
    }
    return {
      enabled: false,
      settings: {
        enabled: false,
        message: "We're working hard to improve your experience!",
        estimatedTime: "2-3 hours"
      }
    };
  };

  const cachedState = getCachedMaintenanceState();
  const [isMaintenanceEnabled, setIsMaintenanceEnabled] = useState(cachedState.enabled);
  const [maintenanceSettings, setMaintenanceSettings] = useState<MaintenanceSettings>(cachedState.settings);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    logger.debug('MaintenanceContext: Loading settings (one-time read)...');
    const settingsRef = doc(db, 'settings', 'maintenance');

    const defaultSettings: MaintenanceSettings = {
      enabled: false,
      message: "We're working hard to improve your experience!",
      estimatedTime: "2-3 hours",
      lastUpdated: new Date().toISOString(),
      updatedBy: 'system'
    };

    const applyAndCache = (settings: MaintenanceSettings) => {
      const enabled = settings?.enabled ?? false;
      setIsMaintenanceEnabled(enabled);
      setMaintenanceSettings(settings);
      try {
        localStorage.setItem('maintenanceState', JSON.stringify({
          enabled,
          settings,
          timestamp: Date.now()
        }));
      } catch (error) {
        logger.debug('MaintenanceContext: Error caching state:', error);
      }
    };

    const run = async () => {
      try {
        const snap = await getDocFromServer(settingsRef).catch(() => getDoc(settingsRef));
        if (snap.exists()) {
          applyAndCache(snap.data() as MaintenanceSettings);
        } else {
          await setDoc(settingsRef, defaultSettings, { merge: true }).catch(() => {});
          applyAndCache(defaultSettings);
        }
      } catch (error) {
        logger.error('MaintenanceContext: Error loading settings:', error);
        applyAndCache(defaultSettings);
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, []);

  const toggleMaintenance = async () => {
    try {
      const settingsRef = doc(db, 'settings', 'maintenance');
      const newValue = !isMaintenanceEnabled;
      
      const updatedSettings: MaintenanceSettings = {
        ...maintenanceSettings,
        enabled: newValue,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      };
      
      await setDoc(settingsRef, updatedSettings);
      setIsMaintenanceEnabled(newValue);
      setMaintenanceSettings(updatedSettings);
      
      // Update cache immediately
      try {
        localStorage.setItem('maintenanceState', JSON.stringify({
          enabled: newValue,
          settings: updatedSettings,
          timestamp: Date.now()
        }));
      } catch (error) {
        logger.debug('MaintenanceContext: Error caching state:', error);
      }
      
      logger.info('MaintenanceContext: Toggled maintenance to:', newValue);
    } catch (error) {
      console.error('Error toggling maintenance setting:', error);
      // Don't throw error to prevent app crash
      logger.warn('MaintenanceContext: Failed to toggle maintenance, continuing with local state');
    }
  };

  const updateMaintenanceSettings = async (newSettings: Partial<MaintenanceSettings>) => {
    try {
      const settingsRef = doc(db, 'settings', 'maintenance');
      
      const updatedSettings: MaintenanceSettings = {
        ...maintenanceSettings,
        ...newSettings,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      };
      
      await setDoc(settingsRef, updatedSettings);
      setMaintenanceSettings(updatedSettings);
      
      // Update cache immediately
      try {
        localStorage.setItem('maintenanceState', JSON.stringify({
          enabled: maintenanceSettings.enabled,
          settings: updatedSettings,
          timestamp: Date.now()
        }));
      } catch (error) {
        logger.debug('MaintenanceContext: Error caching state:', error);
      }
      
      logger.info('MaintenanceContext: Updated maintenance settings:', updatedSettings);
    } catch (error) {
      console.error('Error updating maintenance settings:', error);
      // Don't throw error to prevent app crash
      logger.warn('MaintenanceContext: Failed to update maintenance settings, continuing with local state');
    }
  };

  const value = {
    isMaintenanceEnabled,
    maintenanceSettings,
    toggleMaintenance,
    updateMaintenanceSettings,
    loading
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};
